export const LEARNING_CONTENT_TYPES = [
  'word',
  'character',
  'sentence',
  'grammar',
  'story'
] as const

export const LEARNING_STATUSES = [
  'new',
  'seen',
  'saved',
  'studying',
  'learned',
  'completed'
] as const

export type LearningContentType = (typeof LEARNING_CONTENT_TYPES)[number]
export type LearningStatus = (typeof LEARNING_STATUSES)[number]

export type LearningContentLocator = {
  contentType: LearningContentType
  contentId: string
}

export type LearningStateSnapshot = {
  status?: LearningStatus | null
  familiarity?: number | null
  progress?: number | null
  first_seen_at?: string | null
  last_seen_at?: string | null
  saved_at?: string | null
  learned_at?: string | null
  completed_at?: string | null
}

export type LearningStateResolutionInput = {
  status: LearningStatus
  familiarity?: number | null
  progress?: number | null
}

const STATUS_RANK: Record<LearningStatus, number> = {
  new: 0,
  seen: 1,
  saved: 2,
  studying: 3,
  learned: 4,
  completed: 5
}

function compactId(value: string | number | null | undefined) {
  return String(value ?? '').trim()
}

export function clampLearningValue(value: number | null | undefined) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return Math.min(1, Math.max(0, value))
}

export function mergeLearningStatus(
  current: LearningStatus | null | undefined,
  next: LearningStatus
): LearningStatus {
  if (!current) return next
  return STATUS_RANK[next] >= STATUS_RANK[current] ? next : current
}

export function mergeLearningProgress(
  current: number | null | undefined,
  next: number | null | undefined
) {
  const currentValue = clampLearningValue(current)
  const nextValue = clampLearningValue(next)
  if (currentValue === null) return nextValue
  if (nextValue === null) return currentValue
  return Math.max(currentValue, nextValue)
}

export function resolvePassiveLearningState(
  current: LearningStateSnapshot | null | undefined,
  next: LearningStateResolutionInput
) {
  return {
    status: mergeLearningStatus(current?.status, next.status),
    progress: mergeLearningProgress(current?.progress, next.progress),
    familiarity:
      clampLearningValue(next.familiarity) ?? current?.familiarity ?? null
  }
}

export function resolveExplicitLearningState(
  current: LearningStateSnapshot | null | undefined,
  next: LearningStateResolutionInput,
  now = new Date()
) {
  const timestamp = now.toISOString()
  const hasProgress = Object.prototype.hasOwnProperty.call(next, 'progress')
  const hasFamiliarity = Object.prototype.hasOwnProperty.call(
    next,
    'familiarity'
  )

  const progress =
    next.status === 'completed' && !hasProgress
      ? 1
      : hasProgress
        ? clampLearningValue(next.progress)
        : (current?.progress ?? null)
  const familiarity = hasFamiliarity
    ? clampLearningValue(next.familiarity)
    : (current?.familiarity ?? null)

  return {
    status: next.status,
    progress,
    familiarity,
    first_seen_at:
      current?.first_seen_at ?? (next.status === 'new' ? null : timestamp),
    last_seen_at:
      next.status === 'new' ? (current?.last_seen_at ?? null) : timestamp,
    saved_at:
      next.status === 'saved'
        ? (current?.saved_at ?? timestamp)
        : (current?.saved_at ?? null),
    learned_at: next.status === 'learned' ? timestamp : null,
    completed_at: next.status === 'completed' ? timestamp : null
  }
}

export function learningContentKey(locator: LearningContentLocator) {
  return `${locator.contentType}:${locator.contentId}`
}

export function wordLearningLocator(
  dictEntryId: number
): LearningContentLocator {
  return { contentType: 'word', contentId: compactId(dictEntryId) }
}

export function characterLearningLocator(
  character: string
): LearningContentLocator {
  return { contentType: 'character', contentId: compactId(character) }
}

export function grammarLearningLocator(
  conceptId: string
): LearningContentLocator {
  return { contentType: 'grammar', contentId: compactId(conceptId) }
}

export function storyLearningLocator(
  storySlug: string
): LearningContentLocator {
  return { contentType: 'story', contentId: compactId(storySlug) }
}

export function sentenceLearningLocator({
  sourceType,
  contentId,
  sentenceId,
  blockId,
  exampleId
}: {
  sourceType: string
  contentId: string
  sentenceId?: string | null
  blockId?: string | null
  exampleId?: string | null
}): LearningContentLocator {
  const sentenceKey =
    compactId(sentenceId) || compactId(blockId) || compactId(exampleId)
  return {
    contentType: 'sentence',
    contentId: [sourceType, contentId, sentenceKey].map(compactId).join(':')
  }
}

export function learningStatusTimestamps(
  status: LearningStatus,
  now = new Date()
) {
  const timestamp = now.toISOString()
  return {
    first_seen_at: status === 'new' ? undefined : timestamp,
    last_seen_at: status === 'new' ? undefined : timestamp,
    saved_at: status === 'saved' ? timestamp : undefined,
    learned_at: status === 'learned' ? timestamp : undefined,
    completed_at: status === 'completed' ? timestamp : undefined
  }
}
